import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createCashFlowEntry,
  createCashFlowEntrySchedule,
  deleteCashFlowEntry,
  fetchCashFlow,
  linkPrepayment,
  markCashFlowEntryPaid,
  removeManualPayments,
  resolveSettlement,
  searchProjects,
  splitCashFlowEntry,
  unlinkPrepayment,
  updateCashFlowEntry,
  type CashFlowRange,
  type CashFlowRequestOptions,
  type CashFlowResponse,
  type CreateCashFlowEntryInput,
  type CreateCashFlowScheduleInput,
  type LinkPrepaymentInput,
  type MarkPaidInput,
  type ProjectSearchOptions,
  type ProjectSummary,
  type ResolveSettlementInput,
  type SplitCashFlowEntryInput,
  type UpdateCashFlowEntryInput,
} from '@/lib/cashFlow';
import {
  fetchCashFlowEntryDetail,
  fetchCashFlowEntryDocumentHtml,
  updateCashFlowEntryOverride,
  type CashFlowDocumentHtmlPayload,
  type CashFlowEntryDetail,
  type OverridePatch,
} from '@/lib/cashFlowDetail';
import {
  fetchInvoiceLoanLinkContext,
  fetchInstallmentInvoiceCandidates,
  fetchLinkedLoanInvoices,
  linkLoanInvoice,
  unlinkLoanInvoice,
  type LinkLoanInvoiceInput,
} from '@/lib/loanInvoiceLinks';

/**
 * Shared query layer for the cash-flow view (replit.md §1.4 — all state flows
 * through one query layer with shared keys). The window is keyed by its range so
 * every consumer of the same range hits one cache entry; mutations invalidate the
 * whole `cash-flow` key so the table, footer and search all refresh together.
 */
export const cashFlowQueryKey = (range: CashFlowRange = {}) =>
  ['cash-flow', range.from ?? null, range.to ?? null] as const;

export interface UseCashFlowResult {
  data: CashFlowResponse | undefined;
  isLoading: boolean;
  isError: boolean;
}

export function useCashFlow(range: CashFlowRange = {}, options?: CashFlowRequestOptions): UseCashFlowResult {
  const query = useQuery({
    queryKey: cashFlowQueryKey(range),
    queryFn: () => fetchCashFlow(range, options),
  });
  return { data: query.data, isLoading: query.isLoading, isError: query.isError };
}

/** Query key for one entry's ENRICHED-ENTRY detail (replit.md module 1.3). Nested
 *  under `cash-flow` so a window/mutation invalidate also drops a stale detail. */
export const cashFlowEntryDetailQueryKey = (id: string) => ['cash-flow', 'detail', id] as const;

export interface UseCashFlowEntryDetailResult {
  data: CashFlowEntryDetail | undefined;
  isLoading: boolean;
  isError: boolean;
}

/** Read the drill detail for the selected entry. `id` is nullable so a single
 *  hook serves both lenses' shared `selectedEntryDetailId` — the query stays
 *  disabled (no fetch) until an entry is actually selected. */
export function useCashFlowEntryDetail(
  id: string | null,
  options?: CashFlowRequestOptions,
): UseCashFlowEntryDetailResult {
  const query = useQuery({
    queryKey: cashFlowEntryDetailQueryKey(id ?? ''),
    queryFn: () => fetchCashFlowEntryDetail(id!, options),
    enabled: id !== null,
  });
  return { data: query.data, isLoading: query.isLoading, isError: query.isError };
}

/** Query key for one entry's LIVE-fetched Erply printout HTML (module 1.3, right
 *  pane). Nested under the detail key so a detail/window invalidate also drops a
 *  stale printout. */
export const cashFlowEntryDocumentHtmlQueryKey = (id: string) =>
  ['cash-flow', 'detail', id, 'document-html'] as const;

export interface UseCashFlowEntryDocumentHtmlResult {
  data: CashFlowDocumentHtmlPayload | undefined;
  isLoading: boolean;
  isError: boolean;
}

/**
 * LIVE-fetch the selected entry's Erply printout HTML (v1 parity: always fresh, never
 * a stored mirror). `enabled` is driven by the pane (true only for an Erply `html-live`
 * document); `refetchOnMount: 'always'` + `staleTime: 0` force a fresh vendor fetch on
 * every open, so the RIGHT pane never shows a stale printout. `enabled` is required so
 * the single caller (DocumentPane) always states intent — no implicit default fetch.
 */
export function useCashFlowEntryDocumentHtml(
  id: string,
  options: { enabled: boolean } & CashFlowRequestOptions,
): UseCashFlowEntryDocumentHtmlResult {
  const { enabled, ...requestOptions } = options;
  const query = useQuery({
    queryKey: cashFlowEntryDocumentHtmlQueryKey(id),
    queryFn: () => fetchCashFlowEntryDocumentHtml(id, requestOptions),
    enabled,
    refetchOnMount: 'always',
    staleTime: 0,
  });
  return { data: query.data, isLoading: query.isLoading, isError: query.isError };
}

/** Persist a classification correction (Liik / Projekt). On success the whole
 *  `cash-flow` key is invalidated so the detail, both lenses and search refresh. */
export function useUpdateCashFlowEntryOverride(options?: CashFlowRequestOptions) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: OverridePatch }) =>
      updateCashFlowEntryOverride(id, patch, options),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cash-flow'] }),
  });
}

export function useCreateCashFlowEntry(options?: CashFlowRequestOptions) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCashFlowEntryInput) => createCashFlowEntry(input, options),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cash-flow'] }),
  });
}

/** Create a manual entry as a PAYMENT SCHEDULE ("Osadena" — N instalments). Mirrors
 *  {@link useCreateCashFlowEntry}: on success the whole `cash-flow` key is invalidated
 *  so both lenses, the register and search render the ÷ instalments together (L14). */
export function useCreateCashFlowSchedule(options?: CashFlowRequestOptions) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCashFlowScheduleInput) => createCashFlowEntrySchedule(input, options),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cash-flow'] }),
  });
}

/** Delete a manually-created entry. On success the whole `cash-flow` key is
 *  invalidated so both lenses, the register, the footer and search drop it
 *  together (one shared query layer). */
export function useDeleteCashFlowEntry(options?: CashFlowRequestOptions) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteCashFlowEntry(id, options),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cash-flow'] }),
  });
}

export function useUpdateCashFlowEntry(options?: CashFlowRequestOptions) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateCashFlowEntryInput }) =>
      updateCashFlowEntry(id, patch, options),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cash-flow'] }),
  });
}

export function useSplitCashFlowEntry(options?: CashFlowRequestOptions) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: SplitCashFlowEntryInput }) =>
      splitCashFlowEntry(id, input, options),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cash-flow'] }),
  });
}

export function useResolveSettlement(options?: CashFlowRequestOptions) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: ResolveSettlementInput }) =>
      resolveSettlement(id, input, options),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cash-flow'] }),
  });
}

/** "Märgi laekunuks/makstuks" — record a Manuaal settlement event. On success the
 *  whole `cash-flow` key is invalidated so both lenses, the detail and search pick
 *  up the new settlement state together (one shared query layer). */
export function useMarkCashFlowEntryPaid(options?: CashFlowRequestOptions) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: MarkPaidInput }) =>
      markCashFlowEntryPaid(id, input, options),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cash-flow'] }),
  });
}

/** "Tühista käsitsi makse" — retract the entry's Manuaal settlement events (the
 *  mark-paid undo). On success the whole `cash-flow` key is invalidated so both
 *  lenses, the detail and search pick up the re-derived settlement state together
 *  (one shared query layer). */
export function useRemoveManualPayments(options?: CashFlowRequestOptions) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => removeManualPayments(id, options),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cash-flow'] }),
  });
}

/** "Seo ettemaksuga" — link a received prepayment (ETT) to a final invoice (ARV).
 *  On success the whole `cash-flow` key is invalidated so every lens (calendar,
 *  table, register) and the footer pick up the corrected status + netted projection
 *  together — the linkage never lives in one lens only (L14, one shared query layer). */
export function useLinkPrepayment(options?: CashFlowRequestOptions) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ finalId, input }: { finalId: string; input: LinkPrepaymentInput }) =>
      linkPrepayment(finalId, input, options),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cash-flow'] }),
  });
}

/** Remove a prepayment-offset link by its id. On success the whole `cash-flow` key
 *  is invalidated so both lenses + the register revert the final to its full
 *  outstanding and free the prepayment's capacity together (one shared query layer). */
export function useUnlinkPrepayment(options?: CashFlowRequestOptions) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (linkId: string) => unlinkPrepayment(linkId, options),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cash-flow'] }),
  });
}

export const loanInstallmentInvoicesQueryKey = (loanId: string, seq: number) =>
  ['cash-flow', 'loan-installment-invoices', loanId, seq] as const;

export function useLoanInstallmentInvoices(
  loanId: string,
  seq: number,
  options?: CashFlowRequestOptions,
) {
  return useQuery({
    queryKey: [...loanInstallmentInvoicesQueryKey(loanId, seq), 'linked'],
    queryFn: () => fetchLinkedLoanInvoices(loanId, seq, options),
    enabled: loanId.length > 0 && seq > 0,
  });
}

export function useLoanInstallmentInvoiceCandidates(
  loanId: string,
  seq: number,
  options?: CashFlowRequestOptions,
) {
  return useQuery({
    queryKey: [...loanInstallmentInvoicesQueryKey(loanId, seq), 'candidates'],
    queryFn: () => fetchInstallmentInvoiceCandidates(loanId, seq, options),
    enabled: loanId.length > 0 && seq > 0,
  });
}

export function useInvoiceLoanLinkContext(
  entryId: string,
  enabled: boolean,
  options?: CashFlowRequestOptions,
) {
  return useQuery({
    queryKey: ['cash-flow', 'invoice-loan-link-context', entryId],
    queryFn: () => fetchInvoiceLoanLinkContext(entryId, options),
    enabled: enabled && entryId.length > 0,
  });
}

export function useLinkLoanInvoice(options?: CashFlowRequestOptions) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: LinkLoanInvoiceInput) => linkLoanInvoice(input, options),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cash-flow'] }),
  });
}

export function useUnlinkLoanInvoice(options?: CashFlowRequestOptions) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { loanId: string; seq: number; entryId: string }) =>
      unlinkLoanInvoice(input, options),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cash-flow'] }),
  });
}

/** Query key for the searchable Projekt picker — nested under `cash-flow` so a
 *  reclassification (which can mint a new effective project) drops stale results. */
export const projectSearchQueryKey = (query: string) => ['cash-flow', 'projects', query] as const;

export interface UseProjectSearchResult {
  data: ProjectSummary[] | undefined;
  isLoading: boolean;
  isError: boolean;
}

/** Search the distinct EFFECTIVE projects the EnrichmentForm offers (pick an existing
 *  project, never free text). A blank query returns the full capped list. */
export function useProjectSearch(query: string, options?: ProjectSearchOptions): UseProjectSearchResult {
  const search = useQuery({
    queryKey: projectSearchQueryKey(query),
    queryFn: () => searchProjects(query, options),
  });
  return { data: search.data, isLoading: search.isLoading, isError: search.isError };
}
